import React, { useCallback, createContext, useContext } from "react";
import axios from "axios";
import { apiPath } from "../lib/constant";

// Create the context
const UserContext = createContext();

const api = axios.create({
  baseURL: apiPath,
});

// Custom hook to use the user context
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

// Add request interceptor to automatically add auth header
api.interceptors.request.use(
  (config) => {
    if (typeof window !== "undefined") {
      const token = localStorage.getItem("token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// User provider component
export const UserProvider = ({ children }) => {
  /** ---------------------- AUTH FUNCTIONS ---------------------- **/
  const fetchEyelashTemplates = useCallback(async (token, id) => {
    try {
      if (!token || !id) {
        return {
          status: false,
          error: "Missing token or id",
          data: null,
        };
      }

      const res = await api.get(`/templates/subscription/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "*/*",
          "Accept-Language":
            "en-IN,en;q=0.9,hi-IN;q=0.8,hi;q=0.7,en-US;q=0.6,en-GB;q=0.5",
        },
      });

      return {
        status: true,
        data: res.data.data || res.data,
        message: res.data.message || "Templates fetched successfully",
      };
    } catch (error) {
      console.error("Error fetching eyelash templates:", error);
      return {
        status: false,
        error:
          error.response?.data?.message ||
          error.message ||
          "Failed to fetch templates",
        data: null,
      };
    }
  }, []);

  const fetchUserProducts = useCallback(async (token) => {
    try {
      if (!token) {
        return {
          status: false,
          error: "Missing token",
          data: null,
        };
      }

      const res = await api.get(`/user-products`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "*/*",
          "Accept-Language":
            "en-IN,en;q=0.9,hi-IN;q=0.8,hi;q=0.7,en-US;q=0.6,en-GB;q=0.5",
        },
      });

      return {
        status: true,
        data: res.data.data || res.data,
        message: res.data.message || "Products fetched successfully",
      };
    } catch (error) {
      console.error("Error fetching user products:", error);
      return {
        status: false,
        error:
          error.response?.data?.message ||
          error.message ||
          "Failed to fetch products",
        data: null,
      };
    }
  }, []);

  const createSession = useCallback(async (token, sessionData) => {
    try {
      if (!token) {
        return {
          status: false,
          error: "Missing token",
          data: null,
        };
      }

      const res = await api.post(`/sessions`, sessionData, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "*/*",
          "Accept-Language":
            "en-IN,en;q=0.9,hi-IN;q=0.8,hi;q=0.7,en-US;q=0.6,en-GB;q=0.5",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      return {
        status: true,
        data: res.data.data || res.data,
        message: res.data.message || "Session created successfully",
      };
    } catch (error) {
      console.error("Error creating session:", error);

      // Handle validation errors and other error responses
      let errorMessage = "Failed to create session";

      if (error.response?.data) {
        const errorData = error.response.data;

        // Handle validation errors array
        if (Array.isArray(errorData.error)) {
          errorMessage = errorData.error.join(", ");
        }
        // Handle single error message
        else if (errorData.error) {
          errorMessage = errorData.error;
        }
        // Handle message field
        else if (errorData.message) {
          errorMessage = errorData.message;
        }
        // Handle status_code for additional context
        else if (errorData.status_code) {
          errorMessage = `Error ${errorData.status_code}: ${errorMessage}`;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        status: false,
        error: errorMessage,
        data: null,
        statusCode: error.response?.data?.status_code || error.response?.status,
        isLogin: error.response?.data?.isLogin || false,
      };
    }
  }, []);


  const fetchGuidePdfByTemplate = useCallback(async (templateId, lashSize) => {
    try {
      const res = await api.get(
        `/guide-pdfs/user?template_id=${templateId}&lash_size=${lashSize}`
      );
      return {
        status: true,
        data: res.data,
        message: "Guide PDF fetched successfully",
      };
    } catch (error) {
      console.error("Error fetching guide PDF:", error);
      return {
        status: false,
        error:
          error.response?.data?.message ||
          error.message ||
          "Failed to fetch guide PDF",
        data: null,
      };
    }
  }, []);

  const value = {
    // Templates
    fetchEyelashTemplates,
    fetchGuidePdfByTemplate,
    fetchUserProducts,
    createSession,
  };

  try {
    return (
      <UserContext.Provider value={value}>{children}</UserContext.Provider>
    );
  } catch (error) {
    console.error("Error in UserProvider:", error);
    return <div>Error loading app: {error.message}</div>;
  }
};

export default UserContext;
